// AI 管理助理浮窗(右下角可開合)。全域掛載:AppShell 掛一次,經 React portal 渲染到
// document.body,不干擾各 feature 頁版面。
//
// 能力(零 mutate):對話式導覽 + 唯讀查詢。喚起時帶當前頁 page_context;助理回 text 增量
// (streaming)、navigate(渲染成「前往 <label> →」按鈕,點擊跳頁)、tool(查詢中細粒度狀態)。
// role 閘門:對 CanAdminRead 角色可見(canUseAssistant);enabled=false(未設 key)顯示友善
// 未啟用狀態、不給輸入。

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { fetchAssistantConfig, streamAssistantChat, type AssistantChatMessage, type AssistantConfig } from './assistantApi';
import {
  assistantAvailability,
  buildPageContext,
  canUseAssistant,
  makeAssistantMessage,
  makeUserMessage,
  reduceAssistantMessage,
  type ChatMessage,
  type NavAction,
} from './assistantModel';
import './assistant.css';

const GREETING = '你好,我是管理助理。告訴我你想做什麼(例如「查某位使用者」「看韌體釋出」),我可以帶你到對應頁面或幫你查詢。';

export function AssistantWidget() {
  const { role } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState<AssistantConfig | null>(null);
  const [configError, setConfigError] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const loadedConfig = useRef(false);

  // 首次開啟時載一次 config(得知 enabled / 未設 key)。
  useEffect(() => {
    if (!open || loadedConfig.current) return;
    loadedConfig.current = true;
    const ac = new AbortController();
    fetchAssistantConfig(ac.signal)
      .then((cfg) => setConfig(cfg))
      .catch(() => setConfigError(true));
    return () => ac.abort();
  }, [open]);

  // 開窗後自動捲到底。
  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, messages]);

  // 卸載時中止任何進行中的串流。
  useEffect(() => () => abortRef.current?.abort(), []);

  const patchAssistant = useCallback((id: string, fn: (m: ChatMessage) => ChatMessage) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? fn(m) : m)));
  }, []);

  const send = useCallback(() => {
    const text = input.trim();
    if (!text || busy) return;

    const userMsg = makeUserMessage(text);
    const assistantMsg = makeAssistantMessage();
    // 送出的歷史:現有對話 + 本則使用者訊息(assistant 佔位泡泡不入 payload)。
    const history: AssistantChatMessage[] = [...messages, userMsg]
      .filter((m) => !m.streaming && (m.text.length > 0 || m.role === 'user'))
      .map((m) => ({ role: m.role, content: m.text }));

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput('');
    setBusy(true);

    const ac = new AbortController();
    abortRef.current = ac;

    void streamAssistantChat({
      messages: history,
      pageContext: buildPageContext(location.pathname, role),
      signal: ac.signal,
      onEvent: (ev) => patchAssistant(assistantMsg.id, (m) => reduceAssistantMessage(m, ev, role)),
    }).finally(() => {
      // 若串流結束但沒收到 done/error,補收尾。
      patchAssistant(assistantMsg.id, (m) => (m.streaming ? { ...m, streaming: false } : m));
      if (abortRef.current === ac) abortRef.current = null;
      setBusy(false);
    });
  }, [input, busy, messages, location.pathname, role, patchAssistant]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
  }, []);

  const goTo = useCallback(
    (nav: NavAction) => {
      if (!nav.known) return;
      navigate(nav.to);
      setOpen(false);
    },
    [navigate]
  );

  // role 閘門:僅 CanAdminRead 角色可見助理。
  if (!canUseAssistant(role)) return null;

  const availability = assistantAvailability(config);
  const inputDisabled = busy || !availability.available;

  const launcher = (
    <button
      type="button"
      className={`ast-launcher${open ? ' is-open' : ''}`}
      aria-label={open ? '關閉管理助理' : '開啟管理助理'}
      aria-expanded={open}
      onClick={() => setOpen((o) => !o)}
    >
      <span aria-hidden="true">{open ? '✕' : '✦'}</span>
    </button>
  );

  const panel = open && (
    <section className="ast-panel" role="dialog" aria-label="管理助理" aria-modal="false">
      <header className="ast-panel-head">
        <div className="ast-panel-title">
          <span className="ast-panel-badge" aria-hidden="true">✦</span>
          <span>管理助理</span>
        </div>
        <button type="button" className="ast-icon-btn" aria-label="關閉" onClick={() => setOpen(false)}>
          ✕
        </button>
      </header>

      <div className="ast-messages" ref={listRef}>
        <div className="ast-bubble ast-bubble--assistant ast-greeting">{GREETING}</div>

        {messages.map((m) =>
          m.role === 'user' ? (
            <div key={m.id} className="ast-bubble ast-bubble--user">
              {m.text}
            </div>
          ) : (
            <div key={m.id} className="ast-bubble ast-bubble--assistant">
              {m.tools.length > 0 && (
                <div className="ast-tools" aria-label="查詢進度">
                  {m.tools.map((t) => (
                    <span key={t.name} className={`ast-tool${t.status === 'done' ? ' is-done' : ''}`}>
                      <span className="ast-tool-dot" aria-hidden="true" />
                      {t.status === 'done' ? `已查詢 ${t.name}` : `查詢中：${t.name}…`}
                    </span>
                  ))}
                </div>
              )}

              {m.text && <div className="ast-text">{m.text}</div>}
              {m.streaming && !m.text && !m.error && <div className="ast-typing" aria-label="產生中"><span /><span /><span /></div>}

              {m.nav.length > 0 && (
                <div className="ast-nav-actions">
                  {m.nav.map((nav, i) => (
                    <button
                      key={`${m.id}-nav-${i}`}
                      type="button"
                      className="ast-nav-btn"
                      disabled={!nav.known}
                      title={nav.reason ?? undefined}
                      onClick={() => goTo(nav)}
                    >
                      <span className="ast-nav-label">前往 {nav.label}</span>
                      <span className="ast-nav-arrow" aria-hidden="true">→</span>
                    </button>
                  ))}
                </div>
              )}

              {m.error && <div className="ast-error">{m.error.message}</div>}
            </div>
          )
        )}
      </div>

      {availability.available ? (
        <form
          className="ast-composer"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <textarea
            className="ast-input"
            rows={1}
            placeholder="問我，或說你想做什麼…"
            value={input}
            disabled={busy}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            aria-label="輸入訊息"
          />
          {busy ? (
            <button type="button" className="btn-ghost btn-sm ast-send" onClick={stop}>
              停止
            </button>
          ) : (
            <button type="submit" className="btn-primary btn-sm ast-send" disabled={inputDisabled || input.trim().length === 0}>
              送出
            </button>
          )}
        </form>
      ) : (
        <div className="ast-disabled">
          {configError
            ? '無法載入助理設定，請稍後再試。'
            : config
              ? (availability.reason ?? '助理目前無法使用。')
              : '載入助理設定中…'}
        </div>
      )}
    </section>
  );

  return createPortal(
    <div className="ast-root">
      {panel}
      {launcher}
    </div>,
    document.body
  );
}
